import { create } from 'zustand';
import type { Transform, ViewerModelHandle, GizmoMode } from '@mindcloudxai_3d/core';

export type { GizmoMode };

export interface AssetItem {
  id: string;
  name: string;
  transform: Transform;
  /** 模型导入来源，用于在预览/导出 controller 中重放 */
  source?: { url?: string; file?: File };
}

interface AssetLibraryState {
  panelOpen: boolean;
  assets: AssetItem[];
  selectedAssetId: string | null;
  /** 选中是否来自 canvas 拾取（而非 UI 卡片点击）；拾取选中不触发相机聚焦 */
  selectedFromPick: boolean;
  gizmoMode: GizmoMode;

  togglePanel: () => void;
  setPanelOpen: (open: boolean) => void;

  /** 从 viewer model:changed 事件同步模型列表 */
  syncFromModels: (models: ViewerModelHandle[]) => void;

  selectAsset: (id: string | null) => void;
  /** canvas 拾取选中，不触发相机聚焦 */
  selectAssetFromPick: (id: string) => void;

  /** 更新单个 asset 的 transform（本地 UI 即时反馈） */
  updateAssetTransform: (id: string, transform: Transform) => void;

  /** 重命名 asset（仅本地 UI 显示名） */
  renameAsset: (id: string, name: string) => void;

  /** 记录模型导入来源（url/file），供预览/导出重放使用 */
  setAssetSource: (id: string, source: { url?: string; file?: File }) => void;

  /** 设置 gizmo 模式 */
  setGizmoMode: (mode: GizmoMode) => void;

  /** 清空所有 asset（场景切换/dispose 时调用） */
  clearAssets: () => void;
}

export const useAssetLibraryStore = create<AssetLibraryState>((set) => ({
  panelOpen: false,
  assets: [],
  selectedAssetId: null,
  selectedFromPick: false,
  gizmoMode: 'translate',

  togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),
  setPanelOpen: (open) => set({ panelOpen: open }),

  syncFromModels: (models) =>
    set((s) => {
      const newIds = new Set(models.map((m) => m.id));
      return {
        assets: models.map((m) => {
          const existing = s.assets.find((a) => a.id === m.id);
          // 保留用户自定义名称和导入来源，仅在首次同步时使用 viewer 返回的 name
          return {
            id: m.id,
            name: existing?.name ?? m.name,
            transform: m.transform,
            source: existing?.source,
          };
        }),
        // 选中模型已被删除时清除选中，避免 stale ID 残留
        selectedAssetId: newIds.has(s.selectedAssetId ?? '') ? s.selectedAssetId : null,
      };
    }),

  selectAsset: (id) => set({ selectedAssetId: id, selectedFromPick: false }),

  // canvas 拖取选中：标记来自拾取，AssetPanel 据此跳过相机聚焦
  selectAssetFromPick: (id) => set({ selectedAssetId: id, selectedFromPick: true }),

  updateAssetTransform: (id, transform) =>
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, transform } : a)),
    })),

  renameAsset: (id, name) =>
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, name } : a)),
    })),

  setAssetSource: (id, source) =>
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, source } : a)),
    })),

  setGizmoMode: (mode) => set({ gizmoMode: mode }),

  clearAssets: () =>
    set({ assets: [], selectedAssetId: null, selectedFromPick: false, gizmoMode: 'translate' }),
}));
